import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { PersonalFormValuesProps } from './types';
import { useNetworkRequest } from '@mp/hooks/useNetworkRequest';
import { useProNavigate } from '@mp/contexts';
import { useFirebaseContext } from '@mp/hooks/useFirebaseContext';
import { useCurrentUser } from '@mp/hooks/useCurrentUser';
import { toE164 } from '@mp/utils/toPhoneNumber';
import { useAnalytics, useToast } from '@medely/ui-kit';

type SubmitAccountVariables = {
  values: PersonalFormValuesProps;
  phoneNumberChanged: boolean;
};

export const usePersonalFormSubmit = () => {
  const { axios } = useNetworkRequest();
  const { navigate } = useProNavigate();
  const { logout, signInWithToken } = useFirebaseContext();
  const { currentUser } = useCurrentUser();
  const analytics = useAnalytics();
  const queryClient = useQueryClient();
  const toast = useToast();

  const { mutate: onSubmitAccount, isLoading: submitLoading } = useMutation(
    async ({ values, phoneNumberChanged }: SubmitAccountVariables) => {
      // Phone number changes wipe Firebase MFA enrollment server-side, which can
      // invalidate the current session. Fetch the re-sign-in token BEFORE the
      // phone update goes out, while the user is still fully authenticated —
      // requesting it after the update risks the user no longer being valid.
      const reSignInToken = phoneNumberChanged
        ? (await axios.post(`/v3/professional/web_view`)).data.token
        : undefined;
      await axios.put(`/v3/professional/accounts`, {
        account: {
          address_first: values.address_first,
          address_second: values.address_second,
          address_zip: values.address_zip,
          address_city: values.address_city,
          state_id: values.state_id,
          phone_number: values.phone_number,
        },
        professional: { nickname: values.nickname },
      });
      return { reSignInToken };
    },
    {
      onSuccess: async ({ reSignInToken }, { phoneNumberChanged, values }) => {
        toast.success('Profile updated', {
          name: 'check',
        });
        if (phoneNumberChanged) {
          try {
            // Re-sign-in must complete before any query is invalidated/refetched —
            // refetching against the old (now server-invalidated) session here is
            // what was forcing a hard logout.
            await signInWithToken?.(reSignInToken);
            queryClient.invalidateQueries(['currentUser']);
            queryClient.invalidateQueries(['GetCurrentUserForPersonalForm']);
          } catch {
            logout?.();
          }
        }
        if (currentUser?.id) {
          analytics.identify(
            currentUser.id.toString(),
            {
              phone: toE164(values?.phone_number?.toString()),
            },
            {
              integrations: { Intercom: true },
            },
          );
        }
        navigate('Profile');
        queryClient.invalidateQueries(['GetCurrentUserForPersonalForm']);
      },
      onError: (e: AxiosError<{ message?: string }>) => {
        toast.error(e?.response?.data?.message ?? 'Error while updating profile');
      },
    },
  );

  const { mutate: createEmergencyContact, isLoading: createEmergencyLoading } = useMutation(
    (values: PersonalFormValuesProps) =>
      axios.post(`/v3/professional/emergency_contacts`, {
        first_name: values.emergency_first_name,
        last_name: values.emergency_last_name,
        phone_number: values.emergency_phone_number,
      }),
    {
      onSuccess: () => {
        navigate('Profile');
        queryClient.invalidateQueries(['GetEmergencyContactForPersonalForm']);
      },
    },
  );

  const { mutate: updateEmergencyContact, isLoading: updateEmergencyLoading } = useMutation(
    (values: PersonalFormValuesProps) =>
      axios.put(`/v3/professional/emergency_contacts/${values.emergency_id}`, {
        first_name: values.emergency_first_name,
        last_name: values.emergency_last_name,
        phone_number: values.emergency_phone_number,
      }),
    {
      onSuccess: () => {
        navigate('Profile');
        queryClient.invalidateQueries(['GetEmergencyContactForPersonalForm']);
      },
    },
  );

  const isSubmitting = submitLoading || createEmergencyLoading || updateEmergencyLoading;

  return {
    updateEmergencyContact,
    createEmergencyContact,
    onSubmitAccount,
    isSubmitting,
  };
};
